import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Lightbulb, Globe } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";
import { getArticleFr } from "@/data/articlesFr";

const SITE = "https://oceandata.ca";

export default function ArticleViewFr() {
  const { slug } = useParams();
  const article = getArticleFr(slug);
  const { setOpen } = useSimplify();

  useEffect(() => {
    if (!article) return;
    document.title = article.seoTitle;
    document.documentElement.lang = "fr";

    const links = [];
    const addAlternate = (hreflang, href) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = hreflang;
      el.href = href;
      el.setAttribute("data-hreflang-injected", "true");
      document.head.appendChild(el);
      links.push(el);
    };
    addAlternate("fr-CA", `${SITE}/fr/journal/${article.slug}`);
    if (article.enSlug) {
      addAlternate("en-CA", `${SITE}/journal/${article.enSlug}`);
      addAlternate("x-default", `${SITE}/journal/${article.enSlug}`);
    }
    if (article.esSlug) {
      addAlternate("es", `${SITE}/es/journal/${article.esSlug}`);
    }

    return () => {
      document.documentElement.lang = "en";
      links.forEach((el) => el.remove());
    };
  }, [article]);

  if (!article) return <Navigate to="/journal" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        inLanguage: "fr-CA",
        author: { "@type": "Person", name: "Bruno Pouliot", worksFor: { "@type": "Organization", name: "Ocean Data" } },
        publisher: { "@id": `${SITE}/#org` },
        mainEntityOfPage: `${SITE}/fr/journal/${article.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Ocean Data", item: SITE },
          { "@type": "ListItem", position: 2, name: "Le Journal Clarté", item: `${SITE}/journal` },
          { "@type": "ListItem", position: 3, name: article.title, item: `${SITE}/fr/journal/${article.slug}` },
        ],
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-white" lang="fr">
      <article className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-10">
            <Link
              to="/fr/gardez-vos-outils"
              data-testid="article-fr-back-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-navy transition-colors"
            >
              <ArrowLeft size={15} /> Gardez vos outils
            </Link>
            {article.enSlug && (
              <Link
                to={`/journal/${article.enSlug}`}
                data-testid="article-fr-en-version-link"
                hrefLang="en"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand-teal transition-colors"
              >
                <Globe size={13} /> Read in English
              </Link>
            )}
          </div>

          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            {article.pillarLabel} · {article.readTime}
          </p>
          <h1
            data-testid="article-fr-headline"
            className="font-display font-bold tracking-tight leading-[1.08] text-brand-navy text-3xl sm:text-4xl lg:text-5xl mb-8"
          >
            <MaskedLine delay={0.1} testId="article-fr-headline-line">{article.title}</MaskedLine>
          </h1>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-12 italic">
            {article.excerpt}
          </p>

          {article.sections.map((sec, i) => (
            <Reveal key={i}>
              <div className="mb-4">
                {sec.h && (
                  <h2 className="font-display font-semibold tracking-tight text-brand-navy text-xl sm:text-2xl leading-snug mt-10 mb-5">
                    {sec.h}
                  </h2>
                )}
                {sec.paras.map((p, j) => (
                  <p key={j} className="text-base sm:text-lg text-slate-600 leading-relaxed mb-6">
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          ))}

          {/* Made to Stick package */}
          <Reveal>
            <div data-testid="article-fr-stick-panel" className="mt-14 rounded-3xl bg-brand-tealsoft border border-brand-tealborder p-7 sm:p-9">
              <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-6">
                <Lightbulb size={14} /> Pourquoi ce texte reste en tête
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  ["Phrase qui colle", article.stick.copy],
                  ["Idée visuelle", article.stick.visual],
                  ["Film de 15 secondes", article.stick.film],
                  ["Pourquoi ça colle", article.stick.why],
                ].map(([label, value], i) => (
                  <div key={i} className="rounded-xl bg-white/70 border border-brand-tealborder/60 p-5">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-2">{label}</p>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          {/* CTA calme */}
          <Reveal>
            <div className="mt-12 rounded-2xl bg-brand-navy text-white p-8 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <p className="font-display font-medium text-lg leading-snug max-w-sm">
                Voyez les deux ou trois endroits où votre entreprise pourrait se simplifier d'abord.
              </p>
              <button
                data-testid="article-fr-cta-button"
                onClick={() => setOpen(true)}
                className="shrink-0 inline-flex items-center gap-2.5 rounded-full bg-brand-teal text-white text-sm font-semibold px-7 py-3.5 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
              >
                Montrez-moi où simplifier
                <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
